import { supabase, clientLogin as supabaseClientLogin } from './supabaseClient';

export interface AdminUser {
  id: string;
  email: string;
  role: 'admin';
}

export interface ClientUser {
  id: string;
  name: string;
  username: string;
  logo?: string;
  industry?: string;
  contactname?: string;
  contactemail?: string;
  contactphone?: string;
  activeprojects?: number;
  status?: string;
  role: 'client';
}

export type User = AdminUser | ClientUser;

// Admin authentication (using Supabase Auth)
export const adminLogin = async (email: string, password: string): Promise<AdminUser> => {
  const { data, error } = await supabase.auth.signInWithPassword({
    email,
    password,
  });
  
  if (error) {
    throw error;
  }
  
  if (!data.user || !data.session) {
    throw new Error('Authentication failed');
  }
  
  // Check if user has admin role in metadata OR is explicitly allowed via env list/single email/domain
  const metadata = data.user.user_metadata;
  const allowedAdminEmails = (process.env.REACT_APP_ADMIN_EMAILS || '')
    .split(',')
    .map(e => e.trim().toLowerCase())
    .filter(Boolean);
  const singleAdminEmail = (process.env.REACT_APP_ADMIN_EMAIL || '').trim().toLowerCase();
  const adminDomain = (process.env.REACT_APP_ADMIN_DOMAIN || 'marketnest.org').trim().toLowerCase();
  const authedEmail = (data.user.email || '').toLowerCase();
  const isEnvAdmin = allowedAdminEmails.includes(authedEmail) || (!!singleAdminEmail && authedEmail === singleAdminEmail) || (adminDomain && authedEmail.endsWith(`@${adminDomain}`));

  if ((!metadata || metadata.role !== 'admin') && !isEnvAdmin) {
    throw new Error('Unauthorized: Admin access required');
  }
  
  const adminUser: AdminUser = {
    id: data.user.id,
    email: data.user.email || '',
    role: 'admin'
  };
  
  // Store admin user data
  localStorage.setItem('user-type', 'admin');
  localStorage.setItem('admin-user', JSON.stringify(adminUser));
  
  return adminUser;
};

// Client login using Supabase Auth and fetch client by user_id
export const clientLogin = async (username: string, password: string): Promise<ClientUser> => {
  // Use the provided email directly with Supabase Auth
  const email = username; // The form field is labeled "Username" but contains email
  
  console.log('Attempting login with email:', email);
  
  // Guard: prevent admin accounts from using client login
  try {
    const list = (process.env.REACT_APP_ADMIN_EMAILS || '')
      .split(',')
      .map(e => e.trim().toLowerCase())
      .filter(Boolean);
    const single = (process.env.REACT_APP_ADMIN_EMAIL || '').trim().toLowerCase();
    const domain = (process.env.REACT_APP_ADMIN_DOMAIN || 'marketnest.org').trim().toLowerCase();
    const emailLower = email.trim().toLowerCase();
    const isAdmin = list.includes(emailLower) || (!!single && emailLower === single) || (domain && emailLower.endsWith(`@${domain}`));
    if (isAdmin) {
      console.warn('[clientLogin] Admin email used on client login. Use /admin-login instead.');
      const err: any = new Error('ADMIN_ACCOUNT_USE_ADMIN_LOGIN');
      err.code = 'ADMIN_ACCOUNT_USE_ADMIN_LOGIN';
      throw err;
    }
  } catch {}
  
  try {
    // Standard authentication flow with Supabase Auth
    const { data, error } = await supabase.auth.signInWithPassword({ email, password });
    
    if (error) {
      console.error('Supabase Auth error:', error);
      throw error;
    }
    
    if (!data.user || !data.session) {
      console.error('Authentication failed: No user or session data');
      throw new Error('Authentication failed');
    }

    // Fetch client by user_id (Supabase Auth UUID)
    const userId = data.user.id;
    console.log('Successfully authenticated with Supabase Auth. User ID:', userId);
    console.log('Looking up client with user_id:', userId);

    // Try to find the client by user_id on clients table
    const { data: clientData, error: clientError } = await supabase
      .from('clients')
      .select('*')
      .eq('user_id', userId)
      .single();

    console.log('Client lookup result:', clientData, clientError);

    // Normalize client result (treat arrays and empty results as null)
    let client: any = null;
    if (clientData && !Array.isArray(clientData)) {
      client = clientData;
    } else if (Array.isArray(clientData) && clientData.length > 0) {
      client = clientData[0];
      console.log('Extracted client from array:', client);
    }

    // Special case for Altare - if email is hello@joinaltare.com, use client-004
    if (email.toLowerCase() === 'hello@joinaltare.com' && !client) {
      console.log('Special case for Altare detected by email');
      
      const { data: altareData, error: altareError } = await supabase
        .from('clients')
        .select('*')
        .eq('id', 'client-004')
        .single();
        
      if (!altareError && altareData) {
        client = altareData;
        console.log('Found Altare client by ID:', client);
        
        // Update the client record with the user_id for future logins
        const { error: updateError } = await supabase
          .from('clients')
          .update({ user_id: userId })
          .eq('id', 'client-004');
          
        if (updateError) {
          console.error('Error updating Altare with user_id:', updateError);
        } else {
          console.log('Updated Altare client record with user_id:', userId);
        }
      }
    }

    // If no client found by direct user_id, try mapping table client_user_links
    if (!client) {
      console.log('No client found by direct user_id. Checking client_user_links mapping table...');
      const { data: linked, error: linkErr } = await supabase
        .from('client_user_links')
        .select('client_id')
        .eq('user_id', userId)
        .maybeSingle();

      if (!linkErr && linked && (linked as any).client_id) {
        const { data: linkedClient, error: linkedClientErr } = await supabase
          .from('clients')
          .select('*')
          .eq('id', (linked as any).client_id)
          .single();

        if (!linkedClientErr && linkedClient) {
          client = linkedClient as any;
          console.log('Found client via client_user_links mapping:', client.id);
        }
      }
    }

    // If still not found, try by email/username as fallback
    if (!client) {
      console.log('No client found by user_id, trying by email:', email);
      
      // Fix: Use proper Supabase query syntax for OR conditions
      const { data: emailClientData, error: emailClientError } = await supabase
        .from('clients')
        .select('*')
        .or(`username.eq."${email}",contactemail.eq."${email}"`)
        .single();
      
      console.log('Client lookup by email result:', emailClientData, emailClientError);
      
      if (emailClientError || !emailClientData) {
        console.error('No client record found for this user by either user_id or email');
        throw new Error('No client record found for this user');
      }
      
      client = emailClientData;

      // Prefer inserting link in client_user_links (supports multiple users per client)
      try {
        const { error: linkInsertErr } = await supabase
          .from('client_user_links')
          .insert({ client_id: client.id, user_id: userId });
        if (linkInsertErr) {
          console.warn('Could not insert client_user_links mapping (may already exist):', linkInsertErr);
        } else {
          console.log('Created mapping in client_user_links for user:', userId);
        }
      } catch (e) {
        console.warn('Error creating client_user_links mapping:', e);
      }
    }

    // Final validation: ensure we have a client
    if (!client || !client.id) {
      console.error('No client associated with this account after all lookups');
      throw new Error('No client record found for this user');
    }

    // Map to ClientUser (add all fields you need)
    const clientUser: ClientUser = {
      id: client.id,
      name: client.name,
      logo: client.logo,
      industry: client.industry,
      contactname: client.contactname,
      contactemail: client.contactemail,
      contactphone: client.contactphone,
      activeprojects: client.activeprojects,
      status: client.status,
      username: client.username,
      role: 'client',
    };

    localStorage.setItem('user-type', 'client');
    localStorage.setItem('client-user', JSON.stringify(clientUser));
    
    console.log('Successfully logged in as client:', clientUser.id);

    return clientUser;
  } catch (error) {
    console.error('Client login error:', error);
    throw error;
  }
};

// Get current user (admin or client)
export const getCurrentUser = (): User | null => {
  const userType = localStorage.getItem('user-type');
  
  if (userType === 'admin') {
    const adminData = localStorage.getItem('admin-user');
    return adminData ? JSON.parse(adminData) : null;
  } else if (userType === 'client') {
    const clientData = localStorage.getItem('client-user');
    return clientData ? JSON.parse(clientData) : null;
  }
  
  return null;
};

// Check if user is logged in
export const isLoggedIn = (): boolean => {
  return localStorage.getItem('user-type') !== null;
};

// Check if user is admin
export const isAdmin = (): boolean => {
  return localStorage.getItem('user-type') === 'admin';
};

// Check if user is client
export const isClient = (): boolean => {
  return localStorage.getItem('user-type') === 'client';
};

// Logout (admin or client)
export const logout = async (): Promise<void> => {
  const userType = localStorage.getItem('user-type');
  
  // Clear all auth-related localStorage items
  localStorage.removeItem('user-type');
  localStorage.removeItem('admin-user');
  localStorage.removeItem('client-user');
  localStorage.removeItem('client-token');
  
  // If admin, also sign out from Supabase Auth
  if (userType === 'admin') {
    await supabase.auth.signOut();
  }
};

// Set auth header for client requests
export const setClientAuthHeader = () => {
  const clientToken = localStorage.getItem('client-token');
  
  if (clientToken) {
    // Set the Authorization header for all Supabase requests
    supabase.realtime.setAuth(clientToken);
  }
};

// Initialize auth - call this when your app starts
export const initAuth = () => {
  const userType = localStorage.getItem('user-type');
  
  if (userType === 'client') {
    setClientAuthHeader();
  }
};
